import { Alert } from '@slal/ui/display';
import { P } from '@slal/ui/typography';
import { useLocation } from 'react-router-dom';
import { useChartContext, useFormDataContext, useSteps } from '~/hooks';
import { ChartData } from '~/types';
import { formatToGBP, getAgeRange } from '~/utils';
import { CardAccordion } from '../Accordion';
import { JarIcon } from '../Icons';
import { Heading, StyledTextLikeHeading, Text } from '../Typography';
import { useIncomePot } from './hooks';
import {
    ChartContainer,
    ChartWrapper,
    IncomePotHeadingWrapper,
    IncomePotsWrapper,
    JarStack,
    Label,
    LabelWrapper,
    LegendColor,
    LegendItem,
    LegendLabel,
    LegendWrapper,
    Line,
    StyledSuccessIcon,
    StyledWarningIcon,
} from './styles';
import { calculateAxisValue, getAlertColors } from './utils';

export const IncomePots = () => {
    const { pathname } = useLocation();
    const { isOnOrPastAnnuity, isOnOrBeforeAnnuity } = useSteps();
    const { pots, targetIncome } = useFormDataContext();
    const { data } = useChartContext();
    const incomePots = pathname === '/explore' ? pots?.explorePots || [] : pots?.defaultPots || [];
    const alertColors = getAlertColors();
    const singlePot = incomePots.length === 1;

    return (
        <IncomePotsWrapper>
            {incomePots.map((pot, index) => {
                const potTotalIncome = data[index].reduce((acc, item) => acc + Number(item.y), 0);
                const potTargetMet = potTotalIncome >= targetIncome.amount;
                const icon = potTargetMet ? <StyledSuccessIcon /> : <StyledWarningIcon />;
                const backgroundColor = potTargetMet ? alertColors.success : alertColors.warning;

                return (
                    <CardAccordion
                        key={`${pot.startAge}_${index}`}
                        icon={isOnOrPastAnnuity ? icon : undefined}
                        backgroundColor={isOnOrPastAnnuity ? backgroundColor : undefined}
                        headerContent={
                            <>
                                <Text>
                                    Age{' '}
                                    <Text tag="strong">
                                        {getAgeRange(pot.startAge, isOnOrBeforeAnnuity ? null : pot.endAge)}
                                    </Text>
                                </Text>
                                <Text>
                                    <strong>{formatToGBP(potTotalIncome)}</strong> a year
                                </Text>
                            </>
                        }
                        openByDefault={index === 0}
                        canClose={!singlePot}
                    >
                        <IncomePot potIndex={index} />
                    </CardAccordion>
                );
            })}
            {isOnOrPastAnnuity && <TargetStatusAlert />}
        </IncomePotsWrapper>
    );
};

const TargetStatusAlert = () => {
    const { unmetTargetPots, getIncomeStatusContent } = useChartContext();

    return (
        <Alert variant={unmetTargetPots.length > 0 ? 'warning' : 'success'} icon={true} slim={true}>
            <P>{getIncomeStatusContent()}</P>
        </Alert>
    );
};

interface IncomePotProps {
    potIndex: number;
}

export const IncomePot = ({ potIndex }: IncomePotProps) => {
    const { animationRefs, bars, data, maxValue, targetIncome, totalIncome } = useIncomePot(potIndex);
    return (
        <ChartWrapper aria-labelledby="hyd--income-in-retirement">
            <ChartContainer>
                <JarIcon aria-hidden={true}>
                    {totalIncome > 0 &&
                        bars.map(bar => (
                            <JarStack
                                key={`${bar.label}-${bar.y}`}
                                {...bar}
                                height={bar.shouldAnimate ? 0 : bar.barHeight}
                                y={bar.shouldAnimate ? 167 - bar.accumulatedHeight : bar.y}
                                aria-label={bar.label}
                                role="presentation"
                            >
                                {bar.shouldAnimate && (
                                    <>
                                        <animate
                                            ref={el => {
                                                if (el instanceof SVGAnimateElement) {
                                                    animationRefs.current[bar.label] = {
                                                        ...animationRefs.current[bar.label],
                                                        height: el,
                                                    };
                                                }
                                            }}
                                            attributeName="height"
                                            from="0"
                                            to={bar.barHeight}
                                            dur="0.6s"
                                            fill="freeze"
                                            begin="indefinite"
                                            calcMode="spline"
                                            keySplines="0.4 0 0.2 1"
                                        />
                                        <animate
                                            ref={el => {
                                                if (el instanceof SVGAnimateElement) {
                                                    animationRefs.current[bar.label] = {
                                                        ...animationRefs.current[bar.label],
                                                        y: el,
                                                    };
                                                }
                                            }}
                                            attributeName="y"
                                            from={167 - bar.accumulatedHeight}
                                            to={bar.y}
                                            dur="0.6s"
                                            fill="freeze"
                                            begin="indefinite"
                                            calcMode="spline"
                                            keySplines="0.4 0 0.2 1"
                                        />
                                    </>
                                )}
                            </JarStack>
                        ))}
                </JarIcon>
                {!!targetIncome && <TargetIncomeLabel maxValue={maxValue} targetIncome={targetIncome} />}
            </ChartContainer>
            <IncomePotHeadingWrapper>
                <Text id="hyd--income-in-retirement">Your income in retirement</Text>
                <Heading level={3}>{formatToGBP(totalIncome)} a year</Heading>
            </IncomePotHeadingWrapper>
            <Legend data={data} />
        </ChartWrapper>
    );
};

interface LabelProps {
    maxValue: number;
    targetIncome: number;
}

const TargetIncomeLabel = ({ targetIncome, maxValue }: LabelProps) => {
    const yPos = calculateAxisValue('y', targetIncome, maxValue, 167) - 1;
    const xPos = calculateAxisValue('x', targetIncome, maxValue, 103);
    return (
        <LabelWrapper aria-label={`Target ${formatToGBP(targetIncome)}`}>
            <Line yPos={yPos} xPos={xPos} />
            <Label yPos={yPos} xPos={xPos}>
                <StyledTextLikeHeading level={5}>Target</StyledTextLikeHeading>
                <Text weight="bold">{formatToGBP(targetIncome)}</Text>
            </Label>
        </LabelWrapper>
    );
};

const Legend = ({ data }: { data: ChartData[] }) => {
    if (!data?.length) return <Text>No income added</Text>;

    const legendItems = data
        .filter(item => item.y > 0)
        .map((item, index) => (
            <LegendItem key={index} role="listitem" aria-label={`${item.label} ${formatToGBP(item.y!)}`}>
                <LegendLabel>
                    <LegendColor color={item.color} />
                    <Text>{item.label}</Text>
                </LegendLabel>
                <Text>{formatToGBP(item.y!)}</Text>
            </LegendItem>
        ));

    return <LegendWrapper role="list">{legendItems}</LegendWrapper>;
};
